// AID 分配(/aid-allocations)— S31(IA specs/01 §2.2;common/12 v1.1 §4.2.1)。
// 依 PO 把 Admin 已「匯入並 release」的 AID inventory(device-ca#3)分配給
// developer workspace(org/user),受 PO 的 quantity / AID Level / model scope 上限。
// 三動作:建立分配、調整計數、撤銷 headroom;皆 reason+case 必填、二段確認、全鏈 audit。
//
// 誠實邊界:
//  • 後端 = restful#74(/v3/admin/aid-allocations*)已在 main → 本頁「可操作」。
//  • allocated / consumed 為 device-ca live 對帳值;超 PO / scope / batch 未 release
//    由後端 fail closed(422 / 503),UI 直接呈現後端錯誤,不在前端假裝通過。
//  • AID 清冊 / 批次匯入(S28/S29)代理端點(/v3/admin/aids*)尚未落地 → 見同域佔位頁。
//  • RBAC(§3.4):建立/調整/撤銷 = admin + platform-owner;security-officer 唯讀。
//  • ⚠️ allocation 撤銷「不是」dual-control(那是 AID revoke S28);此處為二段確認。

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { ApiError } from '../../lib/api';
import { ErrorState } from '../../components/StateBlock';
import { ConfirmDialog } from '../_shared/ConfirmDialog';
import { formatTimestampUs } from '../../lib/format';
import { roleLabel } from '../users/userModel';
import '../_shared/shared.css';
import './aid.css';
import type { AidAllocation } from './aidAllocationApi';
import { adjustAllocation, createAllocation, listAllocations, revokeAllocation } from './aidAllocationApi';
import {
  ALLOC_STATUS_OPTIONS,
  allocStatusLabel,
  allocStatusTone,
  allocatedPct,
  canAdjustAllocation,
  canAidAllocWrite,
  canRevokeAllocation,
  consumedPct,
  headroom,
  ownerScopeLabel,
  parseScopeCsv,
  summarizeAllocations,
  unconsumed,
  validateCounters,
  validateCreateForm,
} from './aidAllocationModel';

function errorText(e: unknown): string {
  if (e instanceof ApiError) return `${e.message}${e.requestId ? `（request_id: ${e.requestId}）` : ''}`;
  return e instanceof Error ? e.message : '操作失敗';
}

interface CreateState {
  batchRef: string;
  poRef: string;
  orgId: string;
  ownerScope: string;
  levelScope: string;
  modelScope: string;
  quantity: string;
  reason: string;
  caseId: string;
}

const EMPTY_CREATE: CreateState = {
  batchRef: '',
  poRef: '',
  orgId: '',
  ownerScope: '',
  levelScope: '',
  modelScope: '',
  quantity: '',
  reason: '',
  caseId: '',
};

interface AdjustState {
  quantity: string;
  allocated: string;
  consumed: string;
  reason: string;
  caseId: string;
}

type ConfirmKind = 'create' | 'adjust' | 'revoke' | null;

/** 計數三段條:track=quantity、mid=allocated、solid=consumed。 */
function CounterBar({ a }: { a: AidAllocation }) {
  return (
    <div className="aid-bar" title={`已耗用 ${a.consumed} / 已分配 ${a.allocated} / 總量 ${a.quantity}`}>
      <div className="aid-bar-alloc" style={{ width: `${allocatedPct(a)}%` }} />
      <div className="aid-bar-consumed" style={{ width: `${consumedPct(a)}%` }} />
    </div>
  );
}

export default function AidAllocationsPage() {
  const { role } = useAuth();
  const writable = canAidAllocWrite(role);

  // 清單 / 篩選
  const [fBatch, setFBatch] = useState('');
  const [fOwner, setFOwner] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [items, setItems] = useState<AidAllocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [listErr, setListErr] = useState<unknown>(null);

  // 選取 / 面板模式
  const [selectedId, setSelectedId] = useState('');
  const [creating, setCreating] = useState(false);
  const [create, setCreate] = useState<CreateState>(EMPTY_CREATE);
  const [adjust, setAdjust] = useState<AdjustState>({ quantity: '', allocated: '', consumed: '', reason: '', caseId: '' });
  const [revokeReason, setRevokeReason] = useState('');
  const [revokeCase, setRevokeCase] = useState('');

  // 共用
  const [confirm, setConfirm] = useState<ConfirmKind>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [actionErr, setActionErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setListErr(null);
    setNotice('');
    setActionErr('');
    try {
      const rows = await listAllocations({ batch_ref: fBatch, owner_scope: fOwner, status: fStatus });
      setItems(rows);
    } catch (e) {
      setListErr(e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [fBatch, fOwner, fStatus]);

  useEffect(() => {
    void load();
    // 首載;之後由「套用篩選」手動觸發。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = useMemo(() => items.find((a) => a.allocation_id === selectedId) ?? null, [items, selectedId]);
  const summary = useMemo(() => summarizeAllocations(items), [items]);

  // 選取一筆時,把 adjust 表單種入現值。
  function selectRow(a: AidAllocation) {
    if (a.allocation_id === selectedId) {
      setSelectedId('');
      return;
    }
    setCreating(false);
    setSelectedId(a.allocation_id);
    setActionErr('');
    setAdjust({ quantity: String(a.quantity), allocated: String(a.allocated), consumed: String(a.consumed), reason: '', caseId: '' });
    setRevokeReason('');
    setRevokeCase('');
  }

  function openCreate() {
    setCreating(true);
    setSelectedId('');
    setActionErr('');
    setCreate(EMPTY_CREATE);
  }

  const createInput = {
    batchRef: create.batchRef,
    poRef: create.poRef,
    ownerScope: create.ownerScope,
    levelScope: parseScopeCsv(create.levelScope),
    modelScope: parseScopeCsv(create.modelScope),
    quantity: Number(create.quantity),
  };
  const createFormErr = validateCreateForm(createInput);
  const createReady = Boolean(writable && !createFormErr && create.reason.trim() && create.caseId.trim());

  const adjustPatch = {
    quantity: adjust.quantity === '' ? undefined : Number(adjust.quantity),
    allocated: adjust.allocated === '' ? undefined : Number(adjust.allocated),
    consumed: adjust.consumed === '' ? undefined : Number(adjust.consumed),
  };
  // 以現值補齊未填欄再驗不變式(consumed ≤ allocated ≤ quantity)。
  const adjustCountersErr = selected
    ? validateCounters({
        quantity: adjustPatch.quantity ?? selected.quantity,
        allocated: adjustPatch.allocated ?? selected.allocated,
        consumed: adjustPatch.consumed ?? selected.consumed,
      })
    : '選取一筆分配';
  const adjustReady = Boolean(writable && selected && canAdjustAllocation(selected) && !adjustCountersErr && adjust.reason.trim() && adjust.caseId.trim());

  const revokeReady = Boolean(writable && selected && canRevokeAllocation(selected) && revokeReason.trim() && revokeCase.trim());

  async function submitCreate() {
    setBusy(true);
    try {
      const next = await createAllocation(
        {
          batch_ref: create.batchRef.trim(),
          po_ref: create.poRef.trim(),
          org_id: create.orgId.trim() || undefined,
          owner_scope: create.ownerScope.trim(),
          level_scope: createInput.levelScope,
          model_scope: createInput.modelScope,
          quantity: createInput.quantity,
        },
        { reason: create.reason.trim(), case_id: create.caseId.trim() }
      );
      setItems((rows) => [next, ...rows]);
      setConfirm(null);
      setCreating(false);
      setActionErr('');
      setSelectedId(next.allocation_id);
      setNotice(`已建立分配 ${next.allocation_id}(批次 ${next.batch_ref}・workspace ${next.owner_scope}・總量 ${next.quantity})`);
      setCreate(EMPTY_CREATE);
    } catch (e) {
      setActionErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function submitAdjust() {
    if (!selected) return;
    setBusy(true);
    try {
      const next = await adjustAllocation(selected.allocation_id, adjustPatch, { reason: adjust.reason.trim(), case_id: adjust.caseId.trim() });
      setItems((rows) => rows.map((a) => (a.allocation_id === next.allocation_id ? next : a)));
      setConfirm(null);
      setActionErr('');
      setNotice(`已調整分配 ${next.allocation_id}(總量 ${next.quantity}・已分配 ${next.allocated}・已耗用 ${next.consumed}・${allocStatusLabel(next.status)})`);
      setAdjust((s) => ({ ...s, reason: '', caseId: '' }));
    } catch (e) {
      setActionErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function submitRevoke() {
    if (!selected) return;
    setBusy(true);
    try {
      const next = await revokeAllocation(selected.allocation_id, { reason: revokeReason.trim(), case_id: revokeCase.trim() });
      setItems((rows) => rows.map((a) => (a.allocation_id === next.allocation_id ? next : a)));
      setConfirm(null);
      setActionErr('');
      setNotice(`已撤銷分配 ${next.allocation_id}(收回未啟用的餘裕;已耗用 ${next.consumed} 不受影響)`);
      setRevokeReason('');
      setRevokeCase('');
    } catch (e) {
      setActionErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="aid-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">AID 分配</h1>
          <p className="page-subtitle">
            依 PO 把已 release 的 AID inventory 分配給 developer workspace,受 PO 的 quantity / AID Level / model scope 上限。
            Admin 不簽發 —— 只分配發放既有 inventory(鐵則 1)。
          </p>
        </div>
        <div className="adm-actionbar-btns">
          <span className={`pill ${writable ? 'pill-good' : 'pill-neutral'}`}>{role ? roleLabel(role) : '其他角色'}</span>
          {writable && (
            <button className="btn-primary btn-sm" onClick={openCreate} disabled={busy}>
              建立分配
            </button>
          )}
        </div>
      </div>

      {notice && (
        <p className="adm-action-result is-ok" role="status">
          {notice}
        </p>
      )}
      {actionErr && !confirm && (
        <p className="adm-action-result is-err" role="alert">
          {actionErr}
        </p>
      )}

      <div className="kpi-strip" aria-label="分配統計">
        <div className="kpi-card kpi-card--hero">
          <span className="kpi-label">分配總數</span>
          <span className="kpi-value">{summary.total}</span>
          <span className="kpi-foot">目前篩選結果</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">生效中</span>
          <span className="kpi-value">{summary.active}</span>
          <span className="kpi-foot">active(用罄 {summary.exhausted}・撤銷 {summary.revoked})</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">已分配 / 總量</span>
          <span className="kpi-value">
            {summary.totalAllocated}
            <span className="kpi-unit"> / {summary.totalQuantity}</span>
          </span>
          <span className="kpi-foot">保留 AID 數 / PO 授權量</span>
        </div>
        <div className="kpi-card kpi-card--secondary">
          <span className="kpi-label">已耗用(已啟用)</span>
          <span className="kpi-value">{summary.totalConsumed}</span>
          <span className="kpi-foot">device-ca live 對帳值;不回收</span>
        </div>
      </div>

      <p className="aid-gap-note">
        誠實邊界:本頁後端 = restful#74(<span className="adm-mono">/v3/admin/aid-allocations*</span>)已在 main → 可操作。
        <strong>已分配 / 已耗用</strong>為 device-ca live 對帳值;超 PO / level·model scope 超出 PO / batch 未 release 由後端
        fail closed(422 / 503),本頁直接呈現後端錯誤。<strong>AID 清冊(S28)與批次匯入·release(S29)</strong>的 admin 代理端點
        (<span className="adm-mono">/v3/admin/aids*</span>)尚未落地 —— 見同域「AID 清冊」「批次匯入與釋出」佔位頁。
      </p>

      <div className="aid-split">
        <section className="card">
          <div className="section-head">
            <div>
              <h2 className="section-title">分配清單</h2>
              <span className="section-meta">一筆 = 某 released 批次的一部分 保留給一個工作區。</span>
            </div>
            {loading && <span className="pill pill-neutral">查詢中</span>}
          </div>

          <div className="aid-filter-row">
            <label className="adm-field">
              <span className="adm-field-label text-secondary">批次參照</span>
              <input value={fBatch} onChange={(e) => setFBatch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void load()} placeholder="batch-…" />
            </label>
            <label className="adm-field">
              <span className="adm-field-label text-secondary">工作區</span>
              <input value={fOwner} onChange={(e) => setFOwner(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void load()} placeholder="org#… / user#…" />
            </label>
            <label className="adm-field">
              <span className="adm-field-label text-secondary">狀態</span>
              <select value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
                {ALLOC_STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <button className="btn-primary btn-sm" disabled={loading} onClick={() => void load()}>
              套用篩選
            </button>
          </div>

          {listErr ? (
            <ErrorState error={listErr} onRetry={() => void load()} />
          ) : items.length > 0 ? (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>分配 / 批次</th>
                    <th>工作區</th>
                    <th>PO</th>
                    <th>用量(耗用/分配/總量)</th>
                    <th>狀態</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((a) => (
                    <tr key={a.allocation_id} className={`clickable ${selectedId === a.allocation_id ? 'is-selected' : ''}`} onClick={() => selectRow(a)}>
                      <td>
                        <div className="adm-mono" title={a.allocation_id}>
                          {a.allocation_id.slice(0, 16)}…
                        </div>
                        <div className="text-tertiary adm-mono">{a.batch_ref}</div>
                      </td>
                      <td className="adm-mono">{a.owner_scope}</td>
                      <td className="adm-mono">{a.po_ref}</td>
                      <td>
                        <div className="aid-counts">
                          <span className="adm-mono">
                            {a.consumed}/{a.allocated}/{a.quantity}
                          </span>
                          <CounterBar a={a} />
                        </div>
                      </td>
                      <td>
                        <span className={`pill ${allocStatusTone(a.status)}`}>{allocStatusLabel(a.status)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">📦</div>
              <div className="empty-state-title">沒有符合篩選的分配</div>
              <div className="empty-state-detail">調整批次 / workspace / 狀態篩選,或{writable ? '按右上「建立分配」新增一筆。' : '由 admin / platform-owner 建立分配。'}</div>
            </div>
          )}
        </section>

        <aside className="aid-side">
          {creating ? (
            <section className="card card--primary">
              <div className="section-head">
                <h2 className="section-title">建立分配</h2>
                <button className="btn-ghost btn-sm" onClick={() => setCreating(false)} disabled={busy}>
                  取消
                </button>
              </div>
              <div className="aid-form">
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">批次參照 batch_ref(必填)</span>
                  <input value={create.batchRef} onChange={(e) => setCreate((s) => ({ ...s, batchRef: e.target.value }))} disabled={busy} placeholder="device-ca issuance batch" />
                </label>
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">PO 參照 po_ref(必填)</span>
                  <input value={create.poRef} onChange={(e) => setCreate((s) => ({ ...s, poRef: e.target.value }))} disabled={busy} placeholder="PO-…" />
                </label>
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">工作區 owner_scope(必填)</span>
                  <input value={create.ownerScope} onChange={(e) => setCreate((s) => ({ ...s, ownerScope: e.target.value }))} disabled={busy} placeholder="org#{id} 或 user#{id}" />
                </label>
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">org_id(選填,便於彙總)</span>
                  <input value={create.orgId} onChange={(e) => setCreate((s) => ({ ...s, orgId: e.target.value }))} disabled={busy} placeholder="org-…" />
                </label>
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">AID 等級範圍(逗號分隔,須 ⊆ PO)</span>
                  <input value={create.levelScope} onChange={(e) => setCreate((s) => ({ ...s, levelScope: e.target.value }))} disabled={busy} placeholder="L1, L2" />
                </label>
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">model scope(逗號分隔,須 ⊆ PO)</span>
                  <input value={create.modelScope} onChange={(e) => setCreate((s) => ({ ...s, modelScope: e.target.value }))} disabled={busy} placeholder="cam-a, cam-b" />
                </label>
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">總量 quantity(必填,≥1)</span>
                  <input type="number" min={1} value={create.quantity} onChange={(e) => setCreate((s) => ({ ...s, quantity: e.target.value }))} disabled={busy} placeholder="100" />
                </label>
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">處置理由(必填)</span>
                  <input value={create.reason} onChange={(e) => setCreate((s) => ({ ...s, reason: e.target.value }))} disabled={busy} placeholder="建立此分配的原因" />
                </label>
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">案件編號(必填)</span>
                  <input value={create.caseId} onChange={(e) => setCreate((s) => ({ ...s, caseId: e.target.value }))} disabled={busy} placeholder="CASE-…" />
                </label>
                {createFormErr && (create.batchRef || create.poRef || create.ownerScope || create.quantity) && (
                  <p className="aid-hint is-warn">{createFormErr}</p>
                )}
                <button
                  className="btn-primary"
                  disabled={busy || !createReady}
                  onClick={() => {
                    setActionErr('');
                    setConfirm('create');
                  }}
                >
                  建立分配
                </button>
                <span className="aid-hint text-tertiary">
                  超 PO 授權量 / level·model scope 超出 PO / batch 未 release 由後端 fail closed(422),送出後才能得到權威判定。
                </span>
              </div>
            </section>
          ) : !selected ? (
            <section className="card card--primary">
              <div className="section-head">
                <h2 className="section-title">選取一筆分配</h2>
              </div>
              <p className="aid-hint text-tertiary">在左側點選一筆分配以檢視計數、調整或撤銷{writable ? ',或按右上「建立分配」新增。' : '(目前角色唯讀)。'}</p>
            </section>
          ) : (
            <section className="card card--primary">
              <div className="section-head">
                <h2 className="section-title">分配詳情</h2>
                <span className={`pill ${allocStatusTone(selected.status)}`}>{allocStatusLabel(selected.status)}</span>
              </div>
              <div className="aid-form">
                <div className="aid-kv-grid">
                  <div className="aid-kv">
                    <span className="aid-kv-label">分配 ID</span>
                    <span className="aid-kv-value adm-mono">{selected.allocation_id}</span>
                  </div>
                  <div className="aid-kv">
                    <span className="aid-kv-label">批次 / PO</span>
                    <span className="aid-kv-value adm-mono">
                      {selected.batch_ref} / {selected.po_ref}
                    </span>
                  </div>
                  <div className="aid-kv">
                    <span className="aid-kv-label">工作區</span>
                    <span className="aid-kv-value adm-mono">{ownerScopeLabel(selected.owner_scope)}</span>
                  </div>
                  <div className="aid-kv">
                    <span className="aid-kv-label">AID 等級範圍</span>
                    <span className="aid-kv-value">{selected.level_scope?.join('、') || '—'}</span>
                  </div>
                  <div className="aid-kv">
                    <span className="aid-kv-label">機型範圍</span>
                    <span className="aid-kv-value">{selected.model_scope?.join('、') || '—'}</span>
                  </div>
                  <div className="aid-kv">
                    <span className="aid-kv-label">建立</span>
                    <span className="aid-kv-value">{formatTimestampUs(selected.created_at)}</span>
                  </div>
                </div>

                <div className="aid-counter-panel">
                  <div className="aid-counter-head">
                    <span className="adm-mono">
                      已耗用 {selected.consumed} / 已分配 {selected.allocated} / 總量 {selected.quantity}
                    </span>
                    <span className="text-tertiary">
                      尚可分配 {headroom(selected)}・未 Activate {unconsumed(selected)}
                    </span>
                  </div>
                  <CounterBar a={selected} />
                  <span className="aid-hint text-tertiary">不變式:已耗用 ≤ 已分配 ≤ 總量。已耗用 = 已啟用,永不回收。</span>
                </div>

                {/* 調整 */}
                {canAdjustAllocation(selected) && writable && (
                  <>
                    <div className="section-head" style={{ marginTop: 'var(--spacing-4)' }}>
                      <h3 className="section-title">調整計數</h3>
                    </div>
                    <div className="aid-form-grid">
                      <label className="adm-field">
                        <span className="adm-field-label text-secondary">總量 quantity</span>
                        <input type="number" min={1} value={adjust.quantity} onChange={(e) => setAdjust((s) => ({ ...s, quantity: e.target.value }))} disabled={busy} />
                      </label>
                      <label className="adm-field">
                        <span className="adm-field-label text-secondary">已分配 allocated</span>
                        <input type="number" min={0} value={adjust.allocated} onChange={(e) => setAdjust((s) => ({ ...s, allocated: e.target.value }))} disabled={busy} />
                      </label>
                      <label className="adm-field">
                        <span className="adm-field-label text-secondary">已耗用 consumed</span>
                        <input type="number" min={0} value={adjust.consumed} onChange={(e) => setAdjust((s) => ({ ...s, consumed: e.target.value }))} disabled={busy} />
                      </label>
                    </div>
                    {adjustCountersErr && adjustCountersErr !== '選取一筆分配' && <p className="aid-hint is-warn">{adjustCountersErr}</p>}
                    <div className="aid-form-grid">
                      <label className="adm-field">
                        <span className="adm-field-label text-secondary">處置理由(必填)</span>
                        <input value={adjust.reason} onChange={(e) => setAdjust((s) => ({ ...s, reason: e.target.value }))} disabled={busy} placeholder="調整原因" />
                      </label>
                      <label className="adm-field">
                        <span className="adm-field-label text-secondary">案件編號(必填)</span>
                        <input value={adjust.caseId} onChange={(e) => setAdjust((s) => ({ ...s, caseId: e.target.value }))} disabled={busy} placeholder="CASE-…" />
                      </label>
                    </div>
                    <button
                      className="btn-primary"
                      disabled={busy || !adjustReady}
                      onClick={() => {
                        setActionErr('');
                        setConfirm('adjust');
                      }}
                    >
                      調整
                    </button>
                    <span className="aid-hint text-tertiary">提高總量會重驗 PO 餘裕(後端對 device-ca fail closed)。</span>
                  </>
                )}

                {/* 撤銷 */}
                {canRevokeAllocation(selected) && writable && (
                  <>
                    <div className="section-head" style={{ marginTop: 'var(--spacing-4)' }}>
                      <h3 className="section-title">撤銷分配</h3>
                    </div>
                    <label className="adm-field">
                      <span className="adm-field-label text-secondary">撤銷理由(必填)</span>
                      <input value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)} disabled={busy} placeholder="撤銷此分配的原因" />
                    </label>
                    <label className="adm-field">
                      <span className="adm-field-label text-secondary">案件編號(必填)</span>
                      <input value={revokeCase} onChange={(e) => setRevokeCase(e.target.value)} disabled={busy} placeholder="CASE-…" />
                    </label>
                    <button
                      className="btn-danger"
                      disabled={busy || !revokeReady}
                      onClick={() => {
                        setActionErr('');
                        setConfirm('revoke');
                      }}
                    >
                      撤銷分配
                    </button>
                    <span className="aid-hint text-tertiary">
                      撤銷只收回未啟用的餘裕;已耗用 {selected.consumed} 的 AID 狀態留在 device-ca,不受影響。此非 dual-control(二段確認即可)。
                    </span>
                  </>
                )}

                {selected.status === 'revoked' && (
                  <p className="aid-hint text-tertiary">
                    此分配已於 {formatTimestampUs(selected.revoked_at)} 撤銷(by {selected.revoked_by || '—'})。理由:{selected.revoke_reason || '—'}。
                  </p>
                )}
                {!writable && <p className="aid-hint text-tertiary">建立 / 調整 / 撤銷由 admin / platform-owner 執行(目前角色唯讀)。</p>}
              </div>
            </section>
          )}
        </aside>
      </div>

      {confirm === 'create' && (
        <ConfirmDialog
          title="建立 AID 分配"
          tone="primary"
          confirmLabel="確認建立"
          busy={busy}
          error={actionErr || undefined}
          body={
            <>
              <p>
                將把批次 <span className="adm-mono">{create.batchRef.trim()}</span>(PO <span className="adm-mono">{create.poRef.trim()}</span>)的
                {' '}<strong>{createInput.quantity}</strong> 個 AID 分配給工作區 <span className="adm-mono">{create.ownerScope.trim()}</span>。
              </p>
              <p className="text-tertiary">
                Level {createInput.levelScope.join('、')}・model {createInput.modelScope.join('、')}。超 PO / scope 由後端 fail closed。
              </p>
              <p className="text-tertiary">
                處置理由:{create.reason.trim()}・案件:{create.caseId.trim()}(將寫入稽核 admin.aid_allocation.create,含 actor)
              </p>
            </>
          }
          onConfirm={() => void submitCreate()}
          onCancel={() => setConfirm(null)}
        />
      )}

      {confirm === 'adjust' && selected && (
        <ConfirmDialog
          title="調整分配計數"
          tone="primary"
          confirmLabel="確認調整"
          busy={busy}
          error={actionErr || undefined}
          body={
            <>
              <p>
                分配 <span className="adm-mono">{selected.allocation_id}</span> 計數將調整為:總量{' '}
                <strong>{adjustPatch.quantity ?? selected.quantity}</strong>・已分配{' '}
                <strong>{adjustPatch.allocated ?? selected.allocated}</strong>・已耗用{' '}
                <strong>{adjustPatch.consumed ?? selected.consumed}</strong>。
              </p>
              <p className="text-tertiary">
                處置理由:{adjust.reason.trim()}・案件:{adjust.caseId.trim()}(將寫入稽核 admin.aid_allocation.adjust,含 actor)
              </p>
            </>
          }
          onConfirm={() => void submitAdjust()}
          onCancel={() => setConfirm(null)}
        />
      )}

      {confirm === 'revoke' && selected && (
        <ConfirmDialog
          title="撤銷 AID 分配"
          tone="danger"
          confirmLabel="確認撤銷"
          busy={busy}
          error={actionErr || undefined}
          warning="撤銷會收回此分配尚未啟用的餘裕。已啟用的 AID 狀態留在 device-ca,不受影響、無法由此回收。"
          body={
            <>
              <p>
                將撤銷分配 <span className="adm-mono">{selected.allocation_id}</span>(workspace {selected.owner_scope};尚可分配 {headroom(selected)}、未 Activate {unconsumed(selected)})。
              </p>
              <p className="text-tertiary">
                撤銷理由:{revokeReason.trim()}・案件:{revokeCase.trim()}(將寫入稽核 admin.aid_allocation.revoke,含 actor)
              </p>
            </>
          }
          onConfirm={() => void submitRevoke()}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
